import React, { useState, useEffect } from 'react';
import { selectColumns, getInfo } from '../../utils/api';
import { toast } from 'sonner';
import { useT } from '../../i18n';
import MissingChoice from './MissingChoice';

const SelectColumnsOperation = ({ onSuccess, onCancel }) => {
  const t = useT();
  const [columns, setColumns] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadColumns = async () => {
      try {
        const result = await getInfo();
        const cols = result.info.column_names || [];
        setColumns(cols);
        setSelectedColumns(cols);
      } catch (error) {
        toast.error(t('form.loadColumnsFailed'));
      }
    };
    loadColumns();
  }, []);

  const toggleColumn = (col) => {
    if (selectedColumns.includes(col)) {
      setSelectedColumns(selectedColumns.filter(c => c !== col));
    } else {
      setSelectedColumns([...selectedColumns, col]);
    }
  };

  const selectAll = () => {
    setSelectedColumns(columns);
  };

  const deselectAll = () => {
    setSelectedColumns([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedColumns.length === 0) {
      toast.error(t('select.atLeastOne'));
      return;
    }
    
    setLoading(true);
    try {
      const result = await selectColumns(selectedColumns);
      onSuccess(result);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('select.failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="label">{t('select.toKeep')}</label>
          <div className="flex gap-2">
            <button type="button" onClick={selectAll} className="btn-ghost text-xs h-6">
              {t('form.selectAll')}
            </button>
            <button type="button" onClick={deselectAll} className="btn-ghost text-xs h-6">
              {t('form.deselectAll')}
            </button>
          </div>
        </div>
        <div className="card p-4 max-h-96 overflow-y-auto space-y-2">
          {columns.map(col => (
            <label key={col} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={selectedColumns.includes(col)}
                onChange={() => toggleColumn(col)}
                className="h-4 w-4"
              />
              <span className="text-sm">{col}</span>
            </label>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          {selectedColumns.length} of {columns.length} columns selected
        </p>
      </div>

      <MissingChoice reasons={[selectedColumns.length === 0 && t('select.needColumns')]} />

      <div className="flex gap-2 pt-4 border-t">
        <button type="submit" disabled={loading || selectedColumns.length === 0} className="btn-primary flex-1" data-testid="apply-select-columns">
          {loading ? t('form.applying') : t('select.apply')}
        </button>
        <button type="button" onClick={onCancel} className="btn-secondary">
          {t('form.cancel')}
        </button>
      </div>
    </form>
  );
};

export default SelectColumnsOperation;